// Utilities for parsing and merging structured SSE block events
// Designed to be small, synchronous, and unit-testable.

export function isStructuredBlockEvent(parsed) {
  if (!parsed || typeof parsed !== "object") return false;
  // We accept either direct block objects (with type) or wrapper { block: {...} }
  return !!(parsed.type || parsed.block || parsed.blocks || parsed.id);
}

// Normalize incoming block into a canonical block object
export function normalizeBlock(raw) {
  if (!raw || typeof raw !== "object") return null;
  // Accept either { block: { ... } } or direct block
  const b = raw.block || raw;
  const id = b.id || b.block_id || b.sequence || null;
  return {
    id,
    type: b.type || "paragraph",
    text: b.text || b.content || b.fallbackText || "",
    style: b.style || null,
    items: Array.isArray(b.items) ? b.items.slice() : undefined,
    buttons: Array.isArray(b.buttons) ? b.buttons.map((x) => ({ ...x })) : undefined,
    raw: b
  };
}

// Merge an incoming block fragment into a list of existing blocks.
// Blocks are keyed by id when present, otherwise appended in order.
export function mergeBlocks(existing = [], incoming) {
  if (!incoming) return existing;
  const normalizedIncoming = Array.isArray(incoming)
    ? incoming.map(normalizeBlock).filter(Boolean)
    : [normalizeBlock(incoming)].filter(Boolean);

  if (!normalizedIncoming.length) {
    return existing;
  }

  const result = existing.map(cloneBlock);

  normalizedIncoming.forEach((block) => {
    if (!block) {
      return;
    }

    const targetIndex = resolveMergeIndex(result, block);
    if (targetIndex === -1) {
      result.push(cloneBlock(block));
      return;
    }

    const previous = result[targetIndex] || null;
    result[targetIndex] = mergeBlock(previous, block);
  });

  return result;
}

// Compose fallback text from blocks for older UI or when required.
export function blocksToFallbackText(blocks = []) {
  if (!blocks || !blocks.length) return "";
  return blocks
    .map((b) => {
      if (!b) return "";
      switch (b.type) {
        case "heading":
          return (b.text || "").toUpperCase();
        case "list":
          return (b.items || []).join(", ");
        case "quote":
          return `"${b.text || ""}"`;
        case "divider":
          return "---";
        case "button_group":
          return (b.buttons || []).map((btn) => btn.label).join(" ");
        default:
          return b.text || "";
      }
    })
    .filter(Boolean)
    .join("\n\n");
}

export function mergeBlockText(previous = "", next = "", raw) {
  const prev = typeof previous === "string" ? previous : "";
  const incoming = typeof next === "string" ? next : "";

  if (!incoming) {
    return prev;
  }

  if (!prev) {
    return incoming;
  }

  const trimmedPrev = prev.trim();
  const trimmedNext = incoming.trim();

  if (!trimmedPrev) {
    return incoming;
  }

  if (!trimmedNext) {
    return prev;
  }

  if (raw?.append === true || raw?.mode === "append" || raw?.merge === "append") {
    return prev + incoming;
  }

  if (trimmedPrev === trimmedNext) {
    return prev;
  }

  if (trimmedNext.startsWith(trimmedPrev)) {
    return incoming;
  }

  if (trimmedPrev.startsWith(trimmedNext)) {
    return prev;
  }

  const overlapLength = findOverlapSuffixPrefix(prev, incoming);
  if (overlapLength > 0) {
    return prev + incoming.slice(overlapLength);
  }

  const needsSpace = !prev.endsWith(" ") && !incoming.startsWith(" ");
  return needsSpace ? `${prev} ${incoming}` : prev + incoming;
}

const structuredBlockUtils = {
  isStructuredBlockEvent,
  normalizeBlock,
  mergeBlocks,
  blocksToFallbackText,
  mergeBlockText
};

export default structuredBlockUtils;

function mergeBlock(previous, incoming) {
  const delta = incoming?.raw?.delta;
  const incomingClone = cloneBlock(incoming);

  if (!previous) {
    let base = incomingClone;

    if (delta) {
      const deltaResult = applyBlockDelta(base, null, delta);
      base = deltaResult.block;

      if (!deltaResult.textHandled && incoming.text) {
        base.text = mergeBlockText("", incoming.text, incoming.raw);
      }

      if (!deltaResult.itemsHandled && Array.isArray(incoming.items)) {
        base.items = cloneItems(incoming.items);
      }

      if (!deltaResult.buttonsHandled && Array.isArray(incoming.buttons)) {
        base.buttons = cloneButtons(incoming.buttons);
      }
    } else {
      if (incoming.text) {
        base.text = mergeBlockText("", incoming.text, incoming.raw);
      }

      if (Array.isArray(incoming.items)) {
        base.items = cloneItems(incoming.items);
      }

      if (Array.isArray(incoming.buttons)) {
        base.buttons = cloneButtons(incoming.buttons);
      }
    }

    base.raw = mergeRawBlock(null, incoming.raw);
    return base;
  }

  const previousClone = cloneBlock(previous);
  let merged = {
    ...previousClone,
    ...incomingClone
  };

  let textHandled = false;
  let itemsHandled = false;
  let buttonsHandled = false;

  if (delta) {
    const deltaResult = applyBlockDelta(merged, previousClone, delta);
    merged = deltaResult.block;
    textHandled = deltaResult.textHandled;
    itemsHandled = deltaResult.itemsHandled;
    buttonsHandled = deltaResult.buttonsHandled;
  }

  if (!textHandled) {
    merged.text = mergeBlockText(previousClone.text, incoming.text, incoming.raw);
  }

  if (!itemsHandled) {
    merged.items = mergeListContent(previousClone.items, incoming.items);
  }

  if (!buttonsHandled) {
    merged.buttons = mergeButtonContent(previousClone.buttons, incoming.buttons);
  }

  merged.raw = mergeRawBlock(previousClone.raw, incoming.raw);
  return merged;
}

function resolveMergeIndex(existingBlocks, incoming) {
  if (incoming.id) {
    const byIdIndex = existingBlocks.findIndex((block) => block?.id === incoming.id);
    if (byIdIndex !== -1) {
      return byIdIndex;
    }
  }

  return findIdlessMatch(existingBlocks, incoming);
}

function mergeListContent(previousItems, incomingItems) {
  if (Array.isArray(incomingItems)) {
    return cloneItems(incomingItems);
  }

  if (Array.isArray(previousItems)) {
    return cloneItems(previousItems);
  }

  return undefined;
}

function mergeButtonContent(previousButtons, incomingButtons) {
  if (Array.isArray(incomingButtons)) {
    return cloneButtons(incomingButtons);
  }

  if (Array.isArray(previousButtons)) {
    return cloneButtons(previousButtons);
  }

  return undefined;
}

function mergeRawBlock(previousRaw, nextRaw) {
  if (!previousRaw) {
    return nextRaw || null;
  }

  if (!nextRaw) {
    return previousRaw;
  }

  return {
    ...previousRaw,
    ...nextRaw,
    delta: nextRaw.delta !== undefined ? nextRaw.delta : previousRaw.delta
  };
}

function applyBlockDelta(target, previous, delta) {
  const block = {
    ...target,
    items: Array.isArray(target.items) ? cloneItems(target.items) : target.items,
    buttons: Array.isArray(target.buttons) ? cloneButtons(target.buttons) : target.buttons
  };

  const result = {
    block,
    textHandled: false,
    itemsHandled: false,
    buttonsHandled: false
  };

  if (delta === undefined || delta === null) {
    return result;
  }

  const previousText = previous?.text || "";

  const textOutcome = applyTextDelta(previousText, delta, target.raw);
  if (textOutcome.handled) {
    block.text = textOutcome.value;
    result.textHandled = true;
  }

  if (typeof delta === "object") {
    if (hasOwn(delta, "text")) {
      const nestedText = applyTextDelta(previousText, delta.text, target.raw);
      if (nestedText.handled) {
        block.text = nestedText.value;
        result.textHandled = true;
      }
    }

    if (hasOwn(delta, "items")) {
      const itemsOutcome = applyItemsDelta(previous?.items, delta.items);
      if (itemsOutcome.handled) {
        block.items = itemsOutcome.value;
        result.itemsHandled = true;
      }
    }

    if (hasOwn(delta, "buttons")) {
      const buttonsOutcome = applyButtonsDelta(previous?.buttons, delta.buttons);
      if (buttonsOutcome.handled) {
        block.buttons = buttonsOutcome.value;
        result.buttonsHandled = true;
      }
    }
  }

  return result;
}

function applyTextDelta(previousText = "", operations, raw) {
  if (operations === undefined || operations === null) {
    return { value: previousText, handled: false };
  }

  if (typeof operations === "string") {
    return { value: mergeBlockText(previousText, operations, raw), handled: true };
  }

  if (typeof operations !== "object") {
    return { value: previousText, handled: false };
  }

  if (hasOwn(operations, "replace")) {
    const replacement = deltaValueToString(operations.replace);
    return { value: replacement, handled: true };
  }

  if (hasOwn(operations, "append")) {
    const appendValue = deltaValueToString(operations.append);
    if (!appendValue) {
      return { value: previousText, handled: true };
    }
    return { value: mergeBlockText(previousText, appendValue, { append: true }), handled: true };
  }

  if (hasOwn(operations, "text")) {
    return applyTextDelta(previousText, operations.text, raw);
  }

  const inferred = deltaValueToString(operations);
  if (inferred) {
    return { value: mergeBlockText(previousText, inferred, raw), handled: true };
  }

  return { value: previousText, handled: false };
}

function applyItemsDelta(previousItems, operations) {
  if (operations === undefined || operations === null) {
    return { value: previousItems, handled: false };
  }

  if (Array.isArray(operations)) {
    return { value: cloneItems(operations), handled: true };
  }

  if (typeof operations !== "object") {
    return { value: previousItems, handled: false };
  }

  let result = Array.isArray(previousItems) ? cloneItems(previousItems) : [];
  let handled = false;

  if (Array.isArray(operations.replace)) {
    result = cloneItems(operations.replace);
    handled = true;
  }

  if (Array.isArray(operations.prepend)) {
    const additions = cloneItems(operations.prepend);
    result = mergeCollections(result, additions, itemSignature, "prepend");
    handled = true;
  }

  if (Array.isArray(operations.append)) {
    const additions = cloneItems(operations.append);
    result = mergeCollections(result, additions, itemSignature, "append");
    handled = true;
  }

  return { value: result, handled };
}

function applyButtonsDelta(previousButtons, operations) {
  if (operations === undefined || operations === null) {
    return { value: previousButtons, handled: false };
  }

  if (Array.isArray(operations)) {
    return { value: cloneButtons(operations), handled: true };
  }

  if (typeof operations !== "object") {
    return { value: previousButtons, handled: false };
  }

  let result = Array.isArray(previousButtons) ? cloneButtons(previousButtons) : [];
  let handled = false;

  if (Array.isArray(operations.replace)) {
    result = cloneButtons(operations.replace);
    handled = true;
  }

  if (Array.isArray(operations.prepend)) {
    const additions = cloneButtons(operations.prepend);
    result = mergeCollections(result, additions, buttonSignature, "prepend");
    handled = true;
  }

  if (Array.isArray(operations.append)) {
    const additions = cloneButtons(operations.append);
    result = mergeCollections(result, additions, buttonSignature, "append");
    handled = true;
  }

  return { value: result, handled };
}

function mergeCollections(base, additions, signatureFn, mode = "append") {
  const result = Array.isArray(base) ? base.slice() : [];
  const seen = new Set(result.map((item) => signatureFn(item)).filter(Boolean));

  if (!Array.isArray(additions) || !additions.length) {
    return result;
  }

  const queue = mode === "prepend" ? additions.slice().reverse() : additions;

  queue.forEach((item) => {
    const signature = signatureFn(item);
    if (signature && seen.has(signature)) {
      return;
    }
    if (mode === "prepend") {
      result.unshift(item);
    } else {
      result.push(item);
    }
    if (signature) {
      seen.add(signature);
    }
  });

  return result;
}

function deltaValueToString(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => deltaValueToString(entry)).join("");
  }

  if (typeof value === "object") {
    if (hasOwn(value, "text")) {
      return deltaValueToString(value.text);
    }
    if (hasOwn(value, "content")) {
      return deltaValueToString(value.content);
    }
    if (hasOwn(value, "value")) {
      return deltaValueToString(value.value);
    }
    if (hasOwn(value, "replace") || hasOwn(value, "append")) {
      return deltaValueToString(value.replace ?? value.append);
    }
  }

  return "";
}

function itemSignature(item) {
  if (item && typeof item === "object") {
    if (typeof item.id === "string") {
      return `id:${item.id}`;
    }
    if (typeof item.text === "string") {
      return `text:${normalizeTextForComparison(item.text)}`;
    }
    return `object:${JSON.stringify(item)}`;
  }
  return `value:${normalizeTextForComparison(String(item))}`;
}

function buttonSignature(button) {
  if (button && typeof button === "object") {
    if (typeof button.id === "string") {
      return `id:${button.id}`;
    }
    const label = normalizeTextForComparison(button.label || "");
    const action = normalizeTextForComparison(button.action || "");
    return `button:${label}|${action}`;
  }
  return `value:${normalizeTextForComparison(String(button))}`;
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function findOverlapSuffixPrefix(a = "", b = "") {
  const maxOverlap = Math.min(a.length, b.length);
  for (let length = maxOverlap; length > 0; length -= 1) {
    if (a.slice(-length) === b.slice(0, length)) {
      return length;
    }
  }
  return 0;
}

function cloneBlock(block) {
  if (!block) {
    return block;
  }

  const cloned = {
    ...block
  };

  if (Array.isArray(block.items)) {
    cloned.items = cloneItems(block.items);
  }

  if (Array.isArray(block.buttons)) {
    cloned.buttons = cloneButtons(block.buttons);
  }

  return cloned;
}

function cloneItems(items) {
  return items.map((item) => (typeof item === "object" && item !== null ? { ...item } : item));
}

function cloneButtons(buttons) {
  return buttons.map((button) => ({ ...button }));
}

function findIdlessMatch(existingBlocks, incoming) {
  if (!existingBlocks.length) {
    return -1;
  }

  const normalizedIncomingText = normalizeTextForComparison(incoming.text);
  const incomingItemsSignature = buildItemsSignature(incoming.items);
  const incomingButtonsSignature = buildButtonsSignature(incoming.buttons);
  const slot = incoming.raw?.slot || incoming.raw?.position;

  let bestIndex = -1;
  let bestScore = -1;

  existingBlocks.forEach((candidate, index) => {
    if (!candidate) {
      return;
    }

    if (incoming.id && candidate.id && incoming.id !== candidate.id) {
      return;
    }

    if (candidate.id && candidate.id !== incoming.id) {
      // prefer explicit id matches elsewhere; avoid colliding with keyed entries
      return;
    }

    if (incoming.type && candidate.type && incoming.type !== candidate.type) {
      return;
    }

    if (slot !== undefined && candidate.raw?.slot !== undefined && slot !== candidate.raw?.slot) {
      return;
    }

    const candidateText = normalizeTextForComparison(candidate.text);
    const candidateItemsSignature = buildItemsSignature(candidate.items);
    const candidateButtonsSignature = buildButtonsSignature(candidate.buttons);

    let score = 0;

    if (slot !== undefined && slot === candidate.raw?.slot) {
      score += 4;
    }

    if (normalizedIncomingText && candidateText) {
      if (normalizedIncomingText === candidateText) {
        score += 3;
      } else if (
        normalizedIncomingText.startsWith(candidateText) ||
        candidateText.startsWith(normalizedIncomingText)
      ) {
        score += 2;
      }
    } else if (!normalizedIncomingText && !candidateText) {
      score += 1;
    }

    if (incomingItemsSignature && incomingItemsSignature === candidateItemsSignature) {
      score += 2;
    }

    if (incomingButtonsSignature && incomingButtonsSignature === candidateButtonsSignature) {
      score += 2;
    }

    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });

  return bestScore > 0 ? bestIndex : -1;
}

function normalizeTextForComparison(text) {
  if (typeof text !== "string") {
    return "";
  }
  return text.replace(/\s+/g, " ").trim().toLowerCase();
}

function buildItemsSignature(items) {
  if (!Array.isArray(items) || !items.length) {
    return "";
  }
  return items
    .map((item) => {
      if (typeof item === "string") {
        return normalizeTextForComparison(item);
      }
      if (item && typeof item === "object") {
        if (typeof item.id === "string") {
          return `id:${item.id}`;
        }
        if (typeof item.text === "string") {
          return `text:${normalizeTextForComparison(item.text)}`;
        }
        return JSON.stringify(item);
      }
      return String(item);
    })
    .join("|");
}

function buildButtonsSignature(buttons) {
  if (!Array.isArray(buttons) || !buttons.length) {
    return "";
  }
  return buttons
    .map((button) => normalizeTextForComparison(button?.label || ""))
    .join("|");
}
